import { useEffect, useRef, useState } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { MediaFrame } from './MediaFrame';
import { ResponsiveImage } from './ResponsiveImage';
import { Reveal } from './Reveal';

export function CraftStory({ steps }) {
  const [activeStep, setActiveStep] = useState(0);
  const stepNodes = useRef([]);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;

    const observer = new IntersectionObserver((entries) => {
      const visibleEntry = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top))[0];

      if (visibleEntry) setActiveStep(Number(visibleEntry.target.dataset.storyStep));
    }, {
      rootMargin: '-34% 0px -48% 0px',
      threshold: 0,
    });

    stepNodes.current.filter(Boolean).forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [steps.length]);

  return (
    <section className="craft-story section-pad" aria-labelledby="craft-story-title">
      <div className="section-shell">
        <Reveal className="section-heading craft-story__heading">
          <p className="section-kicker">The work behind the work</p>
          <h2 id="craft-story-title">The finish earns attention. The support beneath it earns trust.</h2>
          <p>A patio, drive, wall, or walkway performs best when the planning and preparation receive the same care as the visible finish.</p>
        </Reveal>

        <div className="craft-story__layout">
          <div className="craft-story__stage" role="group" aria-label="Real Vic's Bricks project work">
            <div className="craft-story__stage-rule" aria-hidden="true">
              <span style={{ '--story-progress': `${((activeStep + 1) / steps.length) * 100}%` }} />
            </div>
            {steps.map((step, index) => (
              <figure
                className={`craft-story__figure${activeStep === index ? ' is-active' : ''}`}
                key={step.title}
                aria-hidden={activeStep !== index}
              >
                <ResponsiveImage
                  src={step.image}
                  alt={activeStep === index ? step.alt : ''}
                  width={step.width}
                  height={step.height}
                  loading="lazy"
                  sizes="(max-width: 1023px) 1px, min(48vw, 660px)"
                  style={{ objectPosition: step.imagePosition }}
                />
                <figcaption>
                  <span>{step.projectType}</span>
                  <strong>{step.photoCaption}</strong>
                  <small>Eastern North Carolina</small>
                </figcaption>
              </figure>
            ))}
            <span className="craft-story__counter" aria-hidden="true">
              {String(activeStep + 1).padStart(2, '0')} <i>/</i> {String(steps.length).padStart(2, '0')}
            </span>
          </div>

          <ol className="craft-story__steps">
            {steps.map((step, index) => (
              <li
                className={activeStep === index ? 'is-active' : ''}
                data-story-step={index}
                key={step.title}
                ref={(node) => { stepNodes.current[index] = node; }}
              >
                <span className="craft-story__step-number">{String(index + 1).padStart(2, '0')}</span>
                <MediaFrame className="craft-story__mobile-media">
                  <ResponsiveImage
                    src={step.image}
                    alt={step.alt}
                    width={step.width}
                    height={step.height}
                    loading="lazy"
                    sizes="(max-width: 767px) calc(100vw - 56px), calc(100vw - 96px)"
                    style={{ objectPosition: step.imagePosition }}
                  />
                </MediaFrame>
                <p className="craft-story__step-kicker">{step.kicker}</p>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>

        <Link className="button button--secondary craft-story__cta" to="/projects">
          Explore real project work <ArrowRight aria-hidden="true" size={17} />
        </Link>
      </div>
    </section>
  );
}
